import React from 'react';
import { createRoot } from 'react-dom/client';
import { calc, defaultState, hasMeaningfulInput } from './calc.js';
import { Btn, CalcOverlayContext, CalcSwitch, LanguageSwitcher, Logo, PrivacyModal } from './components.jsx';
import { CFG } from './config.js';
import { Drawer } from './drawers.jsx';
import { _i18nBoot, currentLocale, t } from './i18n.js';
import { Page1 } from './page1.jsx';
import { Page2 } from './page2.jsx';
import { Page3 } from './page3.jsx';
import { LoadingOverlay, PreStep } from './preStep.jsx';
import { Result } from './result.jsx';
import { LiveEval } from './sidebar.jsx';
const { useState, useEffect, useMemo, useCallback, useRef } = React;

// ============================================================================
// APP — top-level orchestrator
// ============================================================================

// Dev-Mode: nur auf localhost aktiv. Schaltet das Calc-Overlay frei.
const DEV_MODE = typeof location !== 'undefined' && location.hostname === 'localhost';

// SLA wurde v6 von string auf Array umgestellt. Diese Hilfe normalisiert beide Formen,
// damit alte Konfigurationen aus localStorage / Server-Load / AI-Prefill weiter funktionieren.
function _normalizeSla(v) {
  if (Array.isArray(v)) return v.filter(x => x === 'basis' || x === 'weekend' || x === 'vip');
  if (v === 'basis' || v === 'weekend' || v === 'vip') return [v];
  return [];
}

// Security Scanning wurde v7 von boolean auf 3-Paket-Auswahl (yearly/quarterly/monthly) umgestellt.
// Alte boolean-Werte werden gemappt: true -> 'yearly' (günstigstes kostenpflichtiges Paket), false -> 'none'.
function _normalizeSecurityScanning(v) {
  if (v === 'yearly' || v === 'quarterly' || v === 'monthly' || v === 'none') return v;
  if (v === true) return 'yearly';
  return 'none';
}

const STEP_DEFS = [
  { id: 'pre',    num: '0' },
  { id: 'p1',     num: '1' },
  { id: 'p2',     num: '2' },
  { id: 'p3',     num: '3' },
  { id: 'result', num: '4' },
];
const stepLabel = (num) => t(`stepper.${num}`);

function getInitialState() {
  // Try to restore from localStorage
  try {
    const raw = localStorage.getItem('cloudtec.konfig.state.v1');
    if (raw) {
      const s = JSON.parse(raw);
      // Migration: integration direction/criticality IDs an Spec angleichen
      // (in/out/bidi → read/write/bidirectional; low/medium/high → nice-to-have/wichtig/kritisch)
      if (Array.isArray(s?.p2?.integrations)) {
        const dirMap  = { in: 'read', out: 'write', bidi: 'bidirectional' };
        const critMap = { low: 'nice-to-have', medium: 'wichtig', high: 'kritisch' };
        s.p2.integrations = s.p2.integrations.map(i => ({
          ...i,
          direction:   dirMap[i.direction]   || i.direction,
          criticality: critMap[i.criticality] || i.criticality,
        }));
      }
      // v4-Migration: p2.compliance IDs an Backend/Spec angleichen
      // (dsg → revdsg, iso27 → iso27001).
      if (Array.isArray(s?.p2?.compliance)) {
        const complianceMap = { dsg: 'revdsg', iso27: 'iso27001' };
        s.p2.compliance = s.p2.compliance.map(id => complianceMap[id] || id);
      }
      // v4-Migration: p1.ai und p1.stackcms als leere Listen sicherstellen.
      // 'cms' war früher ein Modul, wurde durch Stack CMS ersetzt — alte Auswahl entfernen.
      if (s?.p1) {
        if (!Array.isArray(s.p1.ai))                    s.p1.ai = [];
        if (!Array.isArray(s.p1.stackcms))              s.p1.stackcms = [];
        if (!Array.isArray(s.p1.customCoreFeatures))    s.p1.customCoreFeatures = [];
        if (!Array.isArray(s.p1.customSupportFeatures)) s.p1.customSupportFeatures = [];
        if (Array.isArray(s.p1.modules))                s.p1.modules = s.p1.modules.filter(m => m !== 'cms');
        // v5-Migration: reuseLegacyModules entfernt
        delete s.p1.reuseLegacyModules;
      }
      // v5-Migration: entfernte p2-Felder löschen
      if (s?.p2) {
        delete s.p2.deploymentTopology;
        delete s.p2.iacCiCd;
        delete s.p2.thirdPartyCount;
        delete s.p2.slaThemes;
        delete s.p2.licenseComplexity;
      }
      // v6-Migration: p3.sla wird ein Array (Weekend+VIP kombinierbar, Basis exklusiv)
      if (s?.p3) s.p3.sla = _normalizeSla(s.p3.sla);
      // v7-Migration: p3.securityScanning wird von boolean zu Paket-ID (none|yearly|quarterly|monthly)
      if (s?.p3) s.p3.securityScanning = _normalizeSecurityScanning(s.p3.securityScanning);
      return s;
    }
  } catch (e) {}
  return defaultState();
}
function getInitialStep() {
  try {
    const s = localStorage.getItem('cloudtec.konfig.step.v1');
    if (s && STEP_DEFS.some(x => x.id === s)) return s;
  } catch (e) {}
  return 'pre';
}
function getInitialTheme() {
  try {
    const t = localStorage.getItem('cloudtec.konfig.theme.v1');
    if (t === 'light' || t === 'dark') return t;
  } catch (e) {}
  return 'light';
}

const App = () => {
  const [state, setState] = useState(getInitialState);
  const [step, setStep] = useState(getInitialStep);
  const [theme, setTheme] = useState(getInitialTheme);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingStep, setLoadingStep] = useState(0);
  const [drawer, setDrawer] = useState({ open: false, panel: null });
  const [showHint, setShowHint] = useState(true);
  const [privacyModalOpen, setPrivacyModalOpen] = useState(false);
  const [calcOverlay, setCalcOverlay] = useState(() => {
    if (!DEV_MODE) return false;
    try { return localStorage.getItem('cloudtec.konfig.calcOverlay.v1') === 'true'; } catch (e) { return false; }
  });
  const [, _bump] = useState(0); // forced re-render on locale-changed
  const mainRef = useRef(null);

  useEffect(() => {
    if (!DEV_MODE) return;
    try { localStorage.setItem('cloudtec.konfig.calcOverlay.v1', String(calcOverlay)); } catch (e) {}
  }, [calcOverlay]);

  const overlayValue = useMemo(() => ({ enabled: calcOverlay, baseState: state }), [calcOverlay, state]);

  useEffect(() => {
    const onLocale = () => _bump((n) => n + 1);
    document.addEventListener('locale-changed', onLocale);
    return () => document.removeEventListener('locale-changed', onLocale);
  }, []);

  // Persist — falls LocalStorage voll oder blockiert ist, einmalig warnen
  const localStorageWarned = useRef(false);
  useEffect(() => {
    try {
      localStorage.setItem('cloudtec.konfig.state.v1', JSON.stringify(state));
    } catch (e) {
      if (!localStorageWarned.current) {
        localStorageWarned.current = true;
        console.warn('LocalStorage nicht verfügbar:', e.message);
        // Soft warning — nicht blockend, Konfigurator funktioniert weiter (in-memory)
        setTimeout(() => {
          console.log(t('errors.localstorage_blocked'));
        }, 100);
      }
    }
  }, [state]);
  useEffect(() => {
    try { localStorage.setItem('cloudtec.konfig.step.v1', step); } catch (e) {}
    if (mainRef.current) mainRef.current.scrollTop = 0;
  }, [step]);
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    try { localStorage.setItem('cloudtec.konfig.theme.v1', theme); } catch (e) {}
  }, [theme]);

  // Pre-step validation
  const validatePre = () => {
    const e = {};
    if (!state.pre.title || state.pre.title.trim().length < 2) e.title = true;
    if (!state.pre.description || state.pre.description.trim().length < 8) e.description = true;
    if (!state.pre.platformType) e.platformType = true;
    if (!state.pre.projectKind)  e.projectKind = true;
    if (!state.pre.companyKind)  e.companyKind = true;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // Pre → submit
  const submitPre = ({ skipAi } = {}) => {
    if (!validatePre()) return;
    if (skipAi) { setStep('p1'); return; }
  };

  const requestAi = () => {
    if (!validatePre()) return;
    // Show privacy notice once per session; "Don't ask again" sticks via localStorage.
    const skipModal = localStorage.getItem('cloudtec.konfig.privacy-ack.v1') === 'true';
    if (skipModal) { runAi(); return; }
    setPrivacyModalOpen(true);
  };

  const acknowledgePrivacy = ({ remember } = {}) => {
    if (remember) {
      try { localStorage.setItem('cloudtec.konfig.privacy-ack.v1', 'true'); } catch (e) {}
    }
    setPrivacyModalOpen(false);
    runAi();
  };

  const skipAi = () => {
    setPrivacyModalOpen(false);
    setStep('p1');
  };

  const runAi = async () => {
    if (!validatePre()) return;
    setLoading(true);
    setLoadingStep(0);

    // Rotate the loading-step labels while we wait for the API
    const rotator = setInterval(() => {
      setLoadingStep((s) => Math.min(s + 1, 3));
    }, 1100);

    try {
      const url = CFG.api.base + CFG.api.prefillPath;
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: state.pre.title,
          description: state.pre.description,
          platformType: state.pre.platformType,
          projectKind: state.pre.projectKind,
          companyKind: state.pre.companyKind,
          locale: currentLocale(),
        }),
      });
      clearInterval(rotator);

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        console.error('KI-Vorbefüllung fehlgeschlagen:', err);
        console.log(t('errors.ki_prefill_failed', { message: err.error || `HTTP ${res.status}` }));
        setLoading(false);
        return;
      }

      const ai = await res.json();

      // Merge AI suggestions into state. Each KI-vorbefülltes Feld wird in
      // ki_metadata gemerkt — Confidence-Badges greifen darauf zu.
      setState((prev) => {
        const merged = {
          ...prev,
          p1: { ...prev.p1, ...(ai.p1 || {}) },
          p2: { ...prev.p2, ...(ai.p2 || {}) },
          p3: { ...prev.p3, ...(ai.p3 || {}) },
        };
        merged.p3.sla = _normalizeSla(merged.p3.sla);
        merged.p3.securityScanning = _normalizeSecurityScanning(merged.p3.securityScanning);
        // Snapshot of what the AI proposed — used to detect "unchanged since prefill"
        const aiSnapshot = {
          p1: { ...(ai.p1 || {}) },
          p2: { ...(ai.p2 || {}) },
          p3: { ...(ai.p3 || {}) },
        };
        return {
          ...merged,
          ki_metadata: {
            feldConfidence: ai.feldConfidence || {},
            aiSnapshot,
            begruendung: ai.begruendung || '',
            assessment: ai.assessment || null,
            prefilledAt: new Date().toISOString(),
            model: ai._meta?.model,
            usage: ai._meta?.usage,
          },
        };
      });

      setLoading(false);
      setStep('p1');
    } catch (e) {
      clearInterval(rotator);
      console.error('Network error during prefill:', e);
      console.log(t('errors.ki_network', { message: e.message }));
      setLoading(false);
    }
  };

  const openDrawer = (panel) => setDrawer({ open: true, panel });
  const closeDrawer = () => setDrawer({ open: false, panel: null });

  // Step nav
  const stepIdx = STEP_DEFS.findIndex(s => s.id === step);
  const goNext = () => stepIdx < STEP_DEFS.length - 1 && setStep(STEP_DEFS[stepIdx + 1].id);
  const goPrev = () => stepIdx > 0 && setStep(STEP_DEFS[stepIdx - 1].id);

  // Reset
  const reset = () => {
    if (window.confirm(t('header.reset_confirm'))) {
      setState(defaultState());
      setStep('pre');
      setErrors({});
    }
  };

  // Config code displayed in the footer = the stable server-side key from
  // state.configCode (set after the first auto-save). Empty until then.
  const configCode = state.configCode || null;

  // Auto-save plumbing (refs declared early so handleLoadCode can reset them).
  const saveTimerRef = useRef(null);
  const saveAbortRef = useRef(null);
  const lastSavedJsonRef = useRef(null);
  const _payloadJson = (s) => JSON.stringify({ pre: s.pre, p1: s.p1, p2: s.p2, p3: s.p3 });

  const handleLoadCode = async (code) => {
    const trimmed = (code || '').trim().toUpperCase();
    if (!trimmed) return;
    try {
      const url = `${CFG.api.base}/state/${encodeURIComponent(trimmed)}`;
      const res = await fetch(url);
      if (res.status === 404) {
        console.log(t('errors.load_not_found', { code: trimmed }));
        return;
      }
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        console.log(t('errors.load_failed', { message: err.error || `HTTP ${res.status}` }));
        return;
      }
      const payload = await res.json();
      if (!payload?.state) {
        console.log('Antwort enthielt keine Konfiguration.');
        return;
      }
      // Merge: keep ki_metadata from server, overwrite pre/p1/p2/p3 with loaded values
      const loaded = { ...defaultState(), ...payload.state, configCode: trimmed };
      if (loaded.p3) {
        loaded.p3.sla = _normalizeSla(loaded.p3.sla);
        loaded.p3.securityScanning = _normalizeSecurityScanning(loaded.p3.securityScanning);
      }
      setState(prev => loaded);
      // Prevent auto-save from immediately re-POSTing the just-loaded state.
      lastSavedJsonRef.current = _payloadJson(loaded);
      setStep('p1');
    } catch (e) {
      console.log(t('errors.ki_network', { message: e.message }));
    }
  };

  // Auto-save: silently POST state to the backend ~3s after the last edit.
  // Cheap on every state change (only resets a timer); the actual JSON.stringify
  // and fetch happen once per save attempt. Any in-flight save is aborted when
  // a newer edit comes in so we never have two POSTs racing for the same slot.
  useEffect(() => {
    if (!hasMeaningfulInput(state)) return;

    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      const json = _payloadJson(state);
      if (json === lastSavedJsonRef.current) return;

      if (saveAbortRef.current) saveAbortRef.current.abort();
      saveAbortRef.current = new AbortController();

      try {
        const hasKey = Boolean(state.configCode);
        const url = hasKey
          ? `${CFG.api.base}/state/${encodeURIComponent(state.configCode)}`
          : `${CFG.api.base}/state`;
        const res = await fetch(url, {
          method: hasKey ? 'PUT' : 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ state }),
          signal: saveAbortRef.current.signal,
        });
        if (!res.ok) return;
        const payload = await res.json();
        lastSavedJsonRef.current = json;
        if (payload.key && payload.key !== state.configCode) {
          setState(prev => ({ ...prev, configCode: payload.key }));
        }
      } catch (e) {
        if (e?.name === 'AbortError') return;
        console.warn('[auto-save] failed:', e?.message);
      }
    }, 3000);

    return () => clearTimeout(saveTimerRef.current);
  }, [state]);

  return (
    <CalcOverlayContext.Provider value={overlayValue}>
    <div className="app">
      {/* Header */}
      <div className="app__head">
        <div className="header">
          <div className="header__brand">
            <Logo height={20}/>
            <div className="label">{t('header.brand_line1')}<br/>{t('header.brand_line2')}</div>
          </div>
          <div className="header__steps">
            <div className="stepper" role="tablist" aria-label="Konfigurator Schritte">
              {STEP_DEFS.map((s, i) => {
                const isActive = s.id === step;
                const isDone = i < stepIdx;
                const disabled = i > stepIdx + 1; // can't skip ahead
                return (
                  <React.Fragment key={s.id}>
                    {i > 0 && <span className="stepper__sep"></span>}
                    <button
                      role="tab"
                      aria-selected={isActive}
                      className={`stepper__btn${isActive ? ' is-active' : ''}${isDone ? ' is-done' : ''}`}
                      disabled={disabled}
                      onClick={() => setStep(s.id)}
                    >
                      <span className="ix">{isDone ? <i className="fa-solid fa-check" style={{ fontSize: 9 }}></i> : s.num}</span>
                      <span className="stepper__label">{stepLabel(s.num)}</span>
                    </button>
                  </React.Fragment>
                );
              })}
            </div>
          </div>
          <div className="header__meta">
            {DEV_MODE && <CalcSwitch enabled={calcOverlay} onChange={setCalcOverlay}/>}
            <LanguageSwitcher/>
            <Btn kind="ghost" size="sm" icon="rotate-left" onClick={reset}>{t('header.reset')}</Btn>
            <Btn kind="ghost" size="sm" variant="icon"
              icon={theme === 'dark' ? 'sun' : 'moon'}
              onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
              aria-label={t('header.theme_toggle')}
            />
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="app__body">
        <div className="body">
          <div className="body__main" id="main" ref={mainRef}>
            {step === 'pre'    && <PreStep state={state} setState={setState} onSubmit={submitPre} requestAi={requestAi} errors={errors}/>}
            {step === 'p1'     && <Page1   state={state} setState={setState}/>}
            {step === 'p2'     && <Page2   state={state} setState={setState}/>}
            {step === 'p3'     && <Page3   state={state} setState={setState} openDrawer={openDrawer} calc={calc}/>}
            {step === 'result' && <Result  state={state} setState={setState} calc={calc} goTo={setStep} openDrawer={openDrawer}/>}
          </div>
          <div className="body__side" aria-label={step === 'pre' ? t('live.load_existing') : 'Live-Auswertung'}>
            <LiveEval state={state} calc={calc} step={step} onLoadCode={handleLoadCode}/>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="app__foot">
        <div className="footer">
          {/* LEFT — numeric step counter + active step label */}
          <div className="footer__left">
            <span className="footer__step-counter" aria-label={t('live.step_progress_x_of_y', { current: stepIdx + 1, total: STEP_DEFS.length })}>
              <span className="footer__step-counter__num">
                {String(Math.max(stepIdx, 0) + 1).padStart(2, '0')}
              </span>
              <span className="footer__step-counter__total">/ {String(STEP_DEFS.length).padStart(2, '0')}</span>
              <span className="footer__step-counter__sep">·</span>
              <span className="footer__step-counter__label">{stepLabel(STEP_DEFS[Math.max(stepIdx, 0)].num)}</span>
            </span>
          </div>

          {/* CENTER — Konfig-Code hero pill (only after the first auto-save produced one) */}
          <div className="footer__center">
            {configCode && (
              <span className="footer__code-hero" title={t('footer.config_code_title')}>
                <span className="footer__code-hero__label">{t('footer.code_label')}</span>
                <span className="footer__code-hero__value">{configCode}</span>
                <button
                  type="button"
                  className="footer__code-hero__copy"
                  onClick={() => { try { navigator.clipboard.writeText(configCode); } catch(e){} }}
                  title={t('footer.copy_code')}
                  aria-label={t('footer.copy_code')}
                >
                  <i className="fa-regular fa-copy"></i>
                </button>
              </span>
            )}
          </div>

          {/* RIGHT — paired navigation capsule. pre/result render as a single
              solo button (no capsule), intermediate steps render Back + Next together. */}
          <div className={`footer__right footer__nav${step === 'pre' || step === 'result' ? ' is-solo' : ''}`}>
            {step === 'pre' ? (
              <Btn kind="primary" iconRight="arrow-right" onClick={requestAi} icon="wand-magic-sparkles">
                {t('actions.ki_prefill')}
              </Btn>
            ) : step === 'result' ? (
              <Btn icon="arrow-left" kind="quiet" size="sm" onClick={goPrev}>
                {t('actions.back_to_label', { label: stepLabel(STEP_DEFS[stepIdx - 1].num) })}
              </Btn>
            ) : (
              <React.Fragment>
                <Btn icon="arrow-left" kind="quiet" size="sm" onClick={goPrev}
                  title={t('actions.back_to_label', { label: stepLabel(STEP_DEFS[stepIdx - 1].num) })}
                  aria-label={t('actions.back_to_label', { label: stepLabel(STEP_DEFS[stepIdx - 1].num) })}/>
                <Btn kind="primary" iconRight="arrow-right" size="sm" onClick={goNext}>
                  {step === 'p3' ? t('actions.show_review') : `${t('common.next')}: ${stepLabel(STEP_DEFS[stepIdx + 1].num)}`}
                </Btn>
              </React.Fragment>
            )}
          </div>
        </div>
      </div>

      {/* Loading overlay (KI-Vorbefüllung) */}
      <LoadingOverlay open={loading} currentStep={loadingStep}/>

      {/* Datenschutz-Modal vor KI-Aufruf */}
      <PrivacyModal
        open={privacyModalOpen}
        onAccept={acknowledgePrivacy}
        onSkip={skipAi}
        onClose={() => setPrivacyModalOpen(false)}
      />

      {/* Drawer */}
      <Drawer open={drawer.open} panel={drawer.panel} onClose={closeDrawer}/>
    </div>
    </CalcOverlayContext.Provider>
  );
};

// Mount — wait for i18n bootstrap before first render so t() returns real strings.
// If i18n fails (network/CORS/404), render anyway so the user sees raw keys
// instead of a blank page.
const rootEl = document.getElementById('root');
const root = createRoot(rootEl);
const _boot = (_i18nBoot || Promise.resolve())
  .catch((e) => { console.error('[i18n] bootstrap failed:', e); });
_boot.then(() => {
  try {
    root.render(<App/>);
  } catch (e) {
    console.error('[mount] render failed:', e);
    rootEl.innerHTML = '<pre style="padding:24px;font:13px/1.5 ui-monospace,monospace;color:#a00">Render-Fehler:\n\n' + (e && e.message ? e.message : String(e)) + '\n\nKonsole (F12) für Stacktrace.</pre>';
  }
});
